import ActionButton from "#/components/buttons/ActionButton";
import ExitStatusBadge from "#/components/manifests/ExitStatusBadge";
import HawbSelect from "#/components/manifests/HawbSelect";
import ReleaseBadge from "#/components/manifests/ReleaseBadge";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { HawbOption, HawbRef } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { Accordion, Button, Text } from "@mantine/core";
import { IconExternalLink, IconUnlink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface ShipmentManifestSectionProps {
  /** Consignment stored on the item, or null. */
  storedHawb: HawbRef | null;
  /**
   * Change waiting for Save: undefined = unchanged, null = unlink,
   * an option = link to that consignment.
   */
  pendingHawb: HawbOption | null | undefined;
  onPendingChange: (next: HawbOption | null | undefined) => void;
  disabled: boolean;
}

/** "Shipment Manifest" accordion on the item detail page. */
const ShipmentManifestSection = ({
  storedHawb,
  pendingHawb,
  onPendingChange,
  disabled,
}: ShipmentManifestSectionProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const isChanged =
    pendingHawb !== undefined && (pendingHawb?.id ?? null) !== (storedHawb?.id ?? null);

  return (
    <Accordion.Item value="shipment_manifest">
      <Accordion.Control>{t("items.hawbLink.sectionTitle")}</Accordion.Control>
      <Accordion.Panel>
        <div className="flex flex-col gap-4">
          {storedHawb ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span>
                <span className="font-medium">{t("manifests.columns.awb")}:</span>{" "}
                {storedHawb.awb_number}
              </span>
              <span>
                <span className="font-medium">{t("manifests.columns.hawb")}:</span>{" "}
                {storedHawb.hawb_number || "--"}
              </span>
              <ReleaseBadge releaseBlockers={storedHawb.release_blockers} />
              <ExitStatusBadge status={storedHawb.exit_status} />
              <Button
                variant="transparent"
                size="compact-xs"
                color="black"
                className="underline px-0"
                rightSection={<IconExternalLink size={12} />}
                onClick={() => window.open(`/fleet/shipment-manifests/${storedHawb.id}`, "_blank")}
              >
                {t("items.hawbLink.openConsignment")}
              </Button>
              <PermissionTooltip disabled={!canWrite()}>
                <ActionButton
                  icon={IconUnlink}
                  color="red"
                  size="sm"
                  aria-label={t("items.hawbLink.unlink")}
                  disabled={disabled || !canWrite() || pendingHawb === null}
                  onClick={() => onPendingChange(null)}
                />
              </PermissionTooltip>
            </div>
          ) : (
            <Text size="sm" c="dimmed">
              {t("items.hawbLink.notLinked")}
            </Text>
          )}

          <HawbSelect
            label={storedHawb ? t("items.hawbLink.changeLabel") : t("items.hawbLink.linkLabel")}
            value={pendingHawb ? pendingHawb.id : null}
            onChange={(_, option) => onPendingChange(option ?? undefined)}
            disabled={disabled || !canWrite()}
            clearable
          />

          {isChanged && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              <span>
                {pendingHawb
                  ? t("items.hawbLink.pendingLink", { hawb: formatHawbLabel(pendingHawb) })
                  : t("items.hawbLink.pendingUnlink")}
              </span>
              <Button
                variant="subtle"
                size="compact-xs"
                color="blue"
                onClick={() => onPendingChange(undefined)}
                disabled={disabled}
              >
                {t("items.hawbLink.undo")}
              </Button>
            </div>
          )}
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default ShipmentManifestSection;
